import { ReportProps } from '@wix/fe-essentials/bi';
import {
  cairoApplyButtonClicked,
  cairoChangesBeforeApply,
  cairoDiscardChangesBeforeApply,
} from '@wix/bex-utils/@wix/bi-logger-os-data/v2';
import { withoutDefaults } from '../util';
import { QueryPendingState } from './QueryPendingState';
import { reaction } from 'mobx';
import { Filter } from '../model';

export class QueryPendingStateBIReporter {
  readonly state;

  isClearing: boolean = false;

  constructor(state: QueryPendingState) {
    this.state = state;
  }

  init() {
    const disposers: (() => void)[] = [];
    this.state.trackedFilters.forEach((trackedFilter) => {
      disposers.push(
        reaction(
          () => trackedFilter.pending.value,
          () => {
            if (
              this.isClearing ||
              trackedFilter.pending.value === trackedFilter.origin.value
            ) {
              return;
            }

            this._reportWithDefaultParams(
              withoutDefaults(cairoChangesBeforeApply)({
                fieldName: trackedFilter.pending.name,
                fieldValue:
                  this._getFilterBIValue(trackedFilter.pending) ?? undefined,
              }),
            );
          },
        ),
      );
    });

    return () => {
      disposers.forEach((disposer) => disposer());
    };
  }

  get reportBi() {
    return this.state.toolbar.reportBi;
  }

  _reportWithDefaultParams(reportProps: ReportProps) {
    this.reportBi({
      ...reportProps,
      params: {
        ...reportProps.params,
        feature: 'Filters',
      },
    });
  }

  _getFilterBIValue(filter: Filter<any>) {
    return filter.value != null
      ? filter.isEmpty
        ? null
        : filter.toArray.map(filter.itemKey).toString()
      : null;
  }

  get currentStatus() {
    return this.state.filters.reduce((prev, cur) => {
      return [
        ...prev,
        {
          fieldName: cur.pending.name,
          fieldValue: this._getFilterBIValue(cur.pending),
        },
      ];
    }, [] as { fieldName: string; fieldValue: string | null }[]);
  }

  apply() {
    const currentStatus = this.currentStatus;
    this._reportWithDefaultParams(
      withoutDefaults(cairoApplyButtonClicked)({
        currentStatus: JSON.stringify(currentStatus),
        filteredListSize: this.state.multi.collections.reduce(
          (prev, cur) => prev + cur.result.total,
          0,
        ),
        listSize:
          this.state.multi.collections.reduce(
            (prev, cur) => prev + (cur.result.available ?? 0),
            0,
          ) || undefined,
        numActiveFields: currentStatus.filter((el) => el.fieldValue !== null)
          .length,
        numOptionalFields: currentStatus.length,
      }),
    );
  }

  async clear() {
    this.isClearing = true;
    this._reportWithDefaultParams(
      withoutDefaults(cairoChangesBeforeApply)({
        isClearButton: true,
        numActiveFields: this.currentStatus.filter(
          (el) => el.fieldValue !== null,
        ).length,
      }),
    );
    // Wait for the next tick in order not to send `cairoChangesBeforeApply` if value changed because of clear action
    await new Promise((resolve) => setTimeout(resolve, 0));
    this.isClearing = false;
  }

  discard() {
    const currentStatus = this.currentStatus;
    this._reportWithDefaultParams(
      withoutDefaults(cairoDiscardChangesBeforeApply)({
        currentStatus: JSON.stringify(currentStatus),
        numActiveFields: currentStatus.filter((el) => el.fieldValue !== null)
          .length,
        numOptionalFields: currentStatus.length,
      }),
    );
  }
}
